import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

const ACTION_COLOR = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  edit: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-700",
  stage_change: "bg-purple-100 text-purple-800",
  assign: "bg-amber-100 text-amber-800",
  bulk_status: "bg-purple-100 text-purple-800",
  bulk_field: "bg-blue-100 text-blue-800",
  bulk_branch: "bg-orange-100 text-orange-800",
  bulk_color: "bg-pink-100 text-pink-800",
  bulk_followup: "bg-cyan-100 text-cyan-800",
  bulk_todays_list: "bg-cyan-100 text-cyan-800",
  bulk_whatsapp: "bg-emerald-100 text-emerald-800",
  login: "bg-gray-100 text-gray-700",
  logout: "bg-gray-100 text-gray-700",
  register: "bg-teal-100 text-teal-800",
  call_log: "bg-emerald-100 text-emerald-800",
  repeat_enquiry: "bg-yellow-100 text-yellow-800",
  import: "bg-indigo-100 text-indigo-800",
};
const actionColor = (a) => ACTION_COLOR[a] || "bg-gray-100 text-gray-700";

const PAGE_SIZE = 50;

// IST calendar date string for "today - n days" (matches backend IST windows).
const istDate = (daysAgo = 0) => {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000 - daysAgo * 86400 * 1000);
  return d.toISOString().slice(0, 10);
};

// Entities whose entity_id can deep-link to a page.
const entityLink = (a) =>
  a.entity_id && /\blead\b/.test(a.entity || "") ? `/leads/${a.entity_id}` : null;

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ actions: [], entities: [], users: [] });
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [userId, setUserId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (action !== "all") params.action = action;
    if (entity !== "all") params.entity = entity;
    if (userId !== "all") params.user_id = userId;
    if (from) params.from = from;
    if (to) params.to = to;
    if (search.trim()) params.search = search.trim();
    api.get("/audit-logs", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [action, entity, userId, from, to, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/audit-logs/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  // Any filter change goes back to page 1.
  const withReset = (setter) => (v) => { setter(v); setPage(0); };
  const preset = (days) => { setFrom(istDate(days - 1)); setTo(istDate(0)); setPage(0); };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search user, details, entity, id…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          className="h-9 w-64 bg-white"
          data-testid="audit-search"
        />
        <Select value={action} onValueChange={withReset(setAction)}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="audit-action-filter"><SelectValue placeholder="All Actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {facets.actions.map((a) => <SelectItem key={a} value={a} className="capitalize">{a.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entity} onValueChange={withReset(setEntity)}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="audit-entity-filter"><SelectValue placeholder="All Entities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {facets.entities.map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={userId} onValueChange={withReset(setUserId)}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="audit-user-filter"><SelectValue placeholder="All Users" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {facets.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} className="h-9 w-36 bg-white" data-testid="audit-from" />
        <span className="text-xs text-muted-foreground">to</span>
        <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} className="h-9 w-36 bg-white" data-testid="audit-to" />
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-9 px-2 text-xs" onClick={() => preset(1)}>Today</Button>
          <Button variant="outline" size="sm" className="h-9 px-2 text-xs" onClick={() => preset(7)}>7d</Button>
          <Button variant="outline" size="sm" className="h-9 px-2 text-xs" onClick={() => preset(30)}>30d</Button>
          <Button variant="outline" size="sm" className="h-9 px-2 text-xs" onClick={() => { setFrom(""); setTo(""); setPage(0); }}>All</Button>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="h-9 border-navy/20 text-navy hover:bg-navy/5" data-testid="audit-refresh">
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <ScrollText className="h-3.5 w-3.5" /> {total.toLocaleString()} event{total !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Details</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">No activity logged</td></tr>
              ) : items.map((a) => {
                const link = entityLink(a);
                const isOpen = expanded === a.id;
                return (
                  <React.Fragment key={a.id}>
                    <tr
                      className="cursor-pointer border-b last:border-0 hover:bg-nude/30"
                      onClick={() => setExpanded(isOpen ? null : a.id)}
                      data-testid={`audit-${a.id}`}
                    >
                      <td className="px-4 py-3 align-top"><span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${actionColor(a.action)}`}>{(a.action || "").replace(/_/g, " ")}</span></td>
                      <td className="px-4 py-3 align-top capitalize text-ink">{a.entity}</td>
                      <td className="max-w-xl px-4 py-3 align-top text-muted-foreground">
                        <span className={isOpen ? "" : "line-clamp-2"}>{a.details || "—"}</span>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {a.user_name || "—"}
                        {a.user_role && <span className="ml-1 text-xs capitalize text-muted-foreground/70">({a.user_role.replace(/_/g, " ")})</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-muted-foreground">{formatDateTime(a.created_at)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b bg-cream/30 last:border-0">
                        <td colSpan={5} className="px-6 py-3 text-xs text-muted-foreground">
                          <div className="flex flex-wrap gap-x-8 gap-y-1">
                            {a.entity_id && (
                              <span>
                                Record ID: <span className="font-mono">{a.entity_id}</span>
                                {link && (
                                  <Link to={link} className="ml-2 inline-flex items-center gap-0.5 text-navy underline" onClick={(e) => e.stopPropagation()}>
                                    open lead <ExternalLink className="h-3 w-3" />
                                  </Link>
                                )}
                              </span>
                            )}
                            {a.path && <span>Request: <span className="font-mono">{a.method} {a.path}</span></span>}
                            {a.ip && <span>IP: <span className="font-mono">{a.ip}</span></span>}
                          </div>
                          {a.details && <div className="mt-2 whitespace-pre-wrap break-words text-ink/80">{a.details}</div>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between border-t bg-cream/30 px-4 py-2 text-xs text-muted-foreground">
          <span>Page {page + 1} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="audit-prev">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" className="h-8 px-2" disabled={page + 1 >= pages || loading} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
